import type {
  ListingsAPIRequestBody,
  ListingsAPIResponseBody,
} from '@noths/polaris-client-listings-api';
import { isBrowser } from '@noths/polaris-client-utils';
import { createAsyncThunk } from '@reduxjs/toolkit';

import { adaptResponseFacetsToStore } from 'src/adapters/serviceResponseToStore/listings-api/adaptResponseFacetsToStore';
import { adaptResponseProductsToStore } from 'src/adapters/serviceResponseToStore/listings-api/adaptResponseProductsToStore';
import { adaptStoreToRequestBody } from 'src/adapters/storeToServiceRequest/listings-api/adaptStoreToRequestBody';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import type {
  RequestProductsActionPayload,
  RequestProductsMetadata,
  TriggerName,
} from 'src/redux/products/types';
import { getListingsData } from 'src/services/listings-api/getListingsData';
import type { SponsoredVisitor } from 'src/services/sponsored/sponsoredProducts';
import {
  getBrowserSponsoredVisitor,
  getSponsoredContext,
} from 'src/services/sponsored/sponsoredProducts';
import { PRODUCTS_LIST_DATA_FETCHING_ERROR } from './thunkErrors';

export interface RequestProductsOptions {
  healthCheck?: boolean;
  /**
   * Tracking only. Snapshotted when the request is dispatched, because the pill
   * drawer can be closed before the response lands and reading the open-pill key
   * at that point would attribute the filter to the wrong surface.
   */
  isFacetPillSurface?: boolean;
  /**
   * Tracking only. Set when the route change moved to a different taxonomy
   * listing rather than refining the current one. Both arrive as a facet change,
   * so `triggerName` cannot tell them apart.
   */
  isTaxonomyNavigation?: boolean;
  metadata?: RequestProductsMetadata;
  pageLoading?: number;
  requestBody?: ListingsAPIRequestBody;
  /**
   * The visitor's consent and Mirakl user id for the sponsored products ad
   * request, resolved from the request cookies by the page on the server. In the
   * browser it is read from `document.cookie` and need not be passed.
   */
  sponsoredVisitor?: SponsoredVisitor;
  triggerName?: TriggerName;
}

export const requestProducts = createAsyncThunk<
  RequestProductsActionPayload,
  RequestProductsOptions
>(
  'products/requestProducts',
  async (
    {
      healthCheck,
      isFacetPillSurface,
      isTaxonomyNavigation,
      metadata,
      requestBody,
      sponsoredVisitor,
      triggerName,
    },
    { getState },
  ) => {
    const state = getState() as ReduxApplicationState;
    const visitor = sponsoredVisitor ?? (isBrowser() ? getBrowserSponsoredVisitor() : undefined);

    let listingsDataPromise: Promise<ListingsAPIResponseBody | null>;

    if (healthCheck) {
      listingsDataPromise = import('src/services/listings-api/healthCheckResponseBody').then(
        (mod) => mod.healthCheckResponseBody,
      );
    } else {
      listingsDataPromise = getListingsData({
        body: requestBody ?? adaptStoreToRequestBody(state),
        bot: state.user.bot || false,
        userId: state.user.cognitoID || '',
        visitorId: state.user.gaVisitorId,
        servingConfig: state.productsQuery.servingConfig,
        sponsored: visitor && getSponsoredContext(state, visitor),
      });
    }

    const data = await listingsDataPromise;

    if (!data) {
      throw PRODUCTS_LIST_DATA_FETCHING_ERROR;
    }

    return {
      facets: adaptResponseFacetsToStore({
        listingsFacets: data.facets,
        storeFacets: state.productsQuery.facets,
      }),
      products: adaptResponseProductsToStore({
        products: data.products,
        page: state.productsQuery.page ?? 1,
        pagination: data.pagination,
        metadata: data.metadata,
        term: state.productsQuery.searchTerm!,
        currencyCode: state.user.currencyCode,
      }),
      triggerName,
      metadata,
      isFacetPillSurface,
      isTaxonomyNavigation,
    };
  },
);
